import PropTypes from "prop-types";
import React, { useState, useEffect } from "react";

import {
  IconButton,
  InputAdornment,
  TextField,
  Tooltip,
  makeStyles,
  useTheme,
} from "@material-ui/core";
import InfoIcon from "@material-ui/icons/Info";
import Visibility from "@material-ui/icons/Visibility";
import VisibilityOff from "@material-ui/icons/VisibilityOff";
import PublishIcon from "@material-ui/icons/Publish";
import QrCode from "../../assets/QRCode.svg";
import { StringUtils } from "../../util";
import { NUM_STRING_FORMATS } from "../../constants";
import spTheme from "../../spTheme";

const { formatNumber } = StringUtils;

/*
 * Text field component with custom styling
 */

const useStyles = makeStyles({
  textField: {
    backgroundColor: (props) => props.defaultBackground,
  },
  root: {
    backgroundColor: (props) => props.defaultBackground,
    "&:hover": {
      backgroundColor: (props) => props.overlayBackground,
      // Reset on touch devices, it doesn't add specificity
      "@media (hover: none)": {
        backgroundColor: (props) => props.defaultBackground,
      },
    },
  },
  /* Styles applied to the root element if color secondary. */
  colorSecondary: {
    "&$underline:after": {
      borderBottomColor: (props) => props.overlayBackground,
    },
  },
  underline: {
    "&:after": {
      borderBottom: (props) => `2px solid ${props.underline}`,
    },
    "&:before": {
      borderBottom: (props) => `1px solid ${props.underline}`,
    },
    "&:hover:before": {
      borderBottom: (props) => `1px solid ${props.underline}`,
    },
  },
  tooltip: {
    padding: spTheme.spacing(4, 0),
    maxWidth: 220,
    cursor: "default",
  },
});

export const FilledTextField = ({
  value,
  label,
  type,
  margin,
  fullWidth,
  InputProps,
  InputLabelProps,
  underlineColor,
  mask,
  onChange,
  onBlur,
  onFocus,
  onIconClick,
  tooltipText,
  testIds,
  ...rest
}) => {
  const [showPassword, setShowPassword] = useState(false);
  const [dataValue, setDataValue] = useState(value);
  const [maskValue, setMaskValue] = useState(value);
  const [focused, setFocused] = useState(false);
  const theme = useTheme();
  const underline = underlineColor ?? theme.palette.primary.red;
  const classes = useStyles({
    defaultBackground: theme.palette.background.default,
    overlayBackground: theme.palette.background.overlay,
    underline,
  });

  const id = `${label.replace(/ +/g, "-")}-input-${Math.random().toString(36).substring(7)}`;

  const inputClasses = {
    underline: classes.underline,
    root: classes.root,
  };

  const inputProps = {
    classes: inputClasses,
    ...InputProps,
  };

  if (type === "password") {
    inputProps.endAdornment = (
      <InputAdornment position="end">
        <IconButton
          edge="end"
          aria-label="Toggle password visibility"
          data-testid={testIds.togglePassword}
          onClick={() => setShowPassword(!showPassword)}
        >
          {showPassword ? <VisibilityOff /> : <Visibility />}
        </IconButton>
      </InputAdornment>
    );
  }

  if (type === "qr-scan") {
    inputProps.endAdornment = (
      <InputAdornment style={{ marginTop: rest.multiline ? "-5%" : "0" }} position="end">
        <IconButton
          edge="end"
          aria-label="Scan QR Code"
          data-testid={testIds.qrScan}
          onClick={onIconClick}
        >
          <img src={QrCode} alt="QR" />
        </IconButton>
      </InputAdornment>
    );
  }

  if (type === "tooltip") {
    inputProps.endAdornment = (
      <InputAdornment position="end">
        <Tooltip title={tooltipText}>
          <InfoIcon className={classes.tooltip} data-testid={testIds.tooltip} />
        </Tooltip>
      </InputAdornment>
    );
  }

  if (type === "csv-upload") {
    inputProps.endAdornment = (
      <InputAdornment position="end" style={{ marginTop: rest.multiline ? "-2%" : "0" }}>
        <IconButton
          edge="end"
          aria-label="CSV-File-Upload"
          data-testid={testIds.csvUpload}
          onClick={onIconClick}
        >
          <PublishIcon />
        </IconButton>
      </InputAdornment>
    );
  }

  const unmask = (e) => {
    if (mask) {
      onChange({
        ...e,
        target: { ...e.target, value: formatNumber(e.target.value, mask, true) },
      });
      return formatNumber(e.target.value, mask, true);
    }
    return e.target.value;
  };

  const applyMask = (val) => {
    if (mask) return formatNumber(val, mask);
    return val;
  };

  const focus = (e) => {
    if (onFocus) onFocus(e);
    setFocused(true);
  };

  const blur = (e) => {
    if (onBlur) onBlur(e);
    if (dataValue.match(/\D/g)) setDataValue(unmask(e));
    setFocused(false);
  };

  useEffect(() => {
    setDataValue(value);
  }, [value]);

  useEffect(() => {
    if (mask) setMaskValue(applyMask(dataValue));
  }, [dataValue, mask]);

  return (
    <TextField
      id={id}
      value={mask && !focused ? maskValue : value}
      label={label}
      margin={margin}
      fullWidth={fullWidth}
      {...rest}
      variant="filled"
      type={type === "password" && showPassword ? "text" : type}
      InputLabelProps={{
        shrink: type === "datetime-local" || InputLabelProps?.shrink,
        focused: false,
        ...InputLabelProps,
      }}
      onChange={onChange}
      onBlur={mask ? blur : onBlur}
      onFocus={mask ? focus : onFocus}
      InputProps={inputProps}
      data-testid={testIds.input}
    />
  );
};

FilledTextField.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  label: PropTypes.string,
  type: PropTypes.string,
  margin: PropTypes.string,
  fullWidth: PropTypes.bool,
  InputProps: PropTypes.object,
  InputLabelProps: PropTypes.object,
  underlineColor: PropTypes.string,
  mask: PropTypes.oneOf([null, ...NUM_STRING_FORMATS]),
  onChange: PropTypes.func,
  onBlur: PropTypes.func,
  onFocus: PropTypes.func,
  onIconClick: PropTypes.func,
  tooltipText: PropTypes.object,
  testIds: PropTypes.object,
};

FilledTextField.defaultProps = {
  value: "",
  label: "",
  type: "text",
  margin: "normal",
  fullWidth: true,
  InputProps: {},
  InputLabelProps: {},
  underlineColor: null,
  mask: null,
  onChange: () => {},
  onBlur: null,
  onFocus: null,
  onIconClick: null,
  tooltipText: {},
  testIds: {},
};

export default FilledTextField;
